import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';

export interface User {
  id: string;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  created_at: string;
  provider?: 'email' | 'google' | 'github' | 'apple';
  providerId?: string;
  subscription?: {
    plan: string;
    status: string;
  };
  apiAccess?: {
    hasAccess: boolean;
    requestStatus?: 'pending' | 'approved' | 'rejected';
    apiKeys?: {
      id: string;
      name: string;
      key: string;
      lastUsed?: string;
      createdAt: string;
    }[];
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authMethod: 'email' | 'oauth' | null;
  checkAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithGitHub: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  refreshOAuthTokens: () => Promise<boolean>;
  checkAuthStatus: () => Promise<void>;
}

import { shallow } from 'zustand/shallow';

export type AuthSelector<T> = (state: AuthState) => T;
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      authMethod: null,

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const authMethod = user.app_metadata?.provider === 'email' ? 'email' : 'oauth';
            set({
              user: {
                id: user.id,
                email: user.email!,
                name: user.user_metadata?.full_name || `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim(),
                first_name: user.user_metadata?.first_name,
                last_name: user.user_metadata?.last_name,
                avatar_url: user.user_metadata?.avatar_url,
                created_at: user.created_at,
                provider: (user.app_metadata?.provider as 'email' | 'google' | 'github' | 'apple') || 'email',
              },
              isAuthenticated: true,
              authMethod,
            });
          } else {
            set({ user: null, isAuthenticated: false, authMethod: null });
          }
        } catch (error) {
          set({ user: null, isAuthenticated: false, authMethod: null });
        } finally {
          set({ isLoading: false });
        }
      },

      checkAuthStatus: async () => {
        await get().checkAuth();
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) throw error;

          if (data.user) {
            set({
              user: {
                id: data.user.id,
                email: data.user.email!,
                name: data.user.user_metadata?.full_name || `${data.user.user_metadata?.first_name || ''} ${data.user.user_metadata?.last_name || ''}`.trim(),
                first_name: data.user.user_metadata?.first_name,
                last_name: data.user.user_metadata?.last_name,
                avatar_url: data.user.user_metadata?.avatar_url,
                created_at: data.user.created_at,
                provider: 'email',
              },
              isAuthenticated: true,
              authMethod: 'email',
            });
          }
        } catch (error) {
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      loginWithGoogle: async () => {
        set({ isLoading: true });
        try {
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
            },
          });
          if (error) throw error;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithGitHub: async () => {
        set({ isLoading: true });
        try {
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'github',
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
            },
          });
          if (error) throw error;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithApple: async () => {
        set({ isLoading: true });
        try {
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'apple',
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
            },
          });
          if (error) throw error;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await supabase.auth.signOut();
          set({ user: null, isAuthenticated: false, authMethod: null });
        } catch (error) {
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateUser: (updates: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },

      setLoading: (isLoading: boolean) => set({ isLoading }),

      refreshOAuthTokens: async (): Promise<boolean> => {
        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (error) throw error;
          return data.session !== null;
        } catch (error) {
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated, authMethod: state.authMethod }),
    }
  )
);

// Typed selector helpers to encourage shallow selection and avoid full-store subscriptions
export const useAuthSelector = <T,>(selector: AuthSelector<T>): T => useAuthStore(selector);
export const selectAuthUser: AuthSelector<User | null> = (s) => s.user;
export const selectIsAuthenticated: AuthSelector<boolean> = (s) => s.isAuthenticated;
export const selectAuthLoading: AuthSelector<boolean> = (s) => s.isLoading;
